import React, { useEffect, useMemo, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { format } from 'date-fns';
import { Activity, Calendar, Filter, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CompanyItem {
  id: string;
  name: string;
}

interface BranchItem {
  id: string;
  name: string;
  companyId: string;
  company?: {
    name: string;
  };
}

interface SummaryResponse {
  total: number;
  loginAttempt: number;
  loginSuccess: number;
  logout: number;
  passwordError: number;
  sessionLimit: number;
  limitReached: number;
  other: number;
  lastEventAt: string | null;
}

interface LogItem {
  id: string;
  topics: string;
  message: string;
  username?: string | null;
  ipAddress?: string | null;
  eventType: string;
  rawTime?: string | null;
  occurredAt: string;
  branch: {
    id: string;
    name: string;
    company: {
      id: string;
      name: string;
    };
  };
}

interface EventsResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  items: LogItem[];
}

type Period = '24h' | '7d' | '30d' | 'custom';

type LogsRequestConfig = AxiosRequestConfig & {
  preserveSessionOnAuthError?: boolean;
};

const MONTH_MAP: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
};

const formatLogTime = (rawTime: string | null | undefined, occurredAt: string): string => {
  const fallbackDate = format(new Date(occurredAt), 'yyyy-MM-dd');
  const fallbackFull = format(new Date(occurredAt), 'yyyy-MM-dd HH:mm:ss');

  if (!rawTime) return fallbackFull;

  const pad = (n: string | number) => String(Number(n)).padStart(2, '0');

  // MMM/DD/YYYY HH:MM:SS  ou  MMM-DD-YYYY HH:MM:SS
  const fullAlpha = rawTime.match(/^([a-z]{3})[\/\-](\d{1,2})[\/\-](\d{4})\s+(\d{2}:\d{2}:\d{2})$/i);
  if (fullAlpha) {
    const month = MONTH_MAP[fullAlpha[1].toLowerCase()] ?? pad(fullAlpha[1]);
    return `${fullAlpha[3]}-${month}-${pad(fullAlpha[2])} ${fullAlpha[4]}`;
  }

  // MMM/DD HH:MM:SS  (sem ano)
  const shortAlpha = rawTime.match(/^([a-z]{3})[\/\-](\d{1,2})\s+(\d{2}:\d{2}:\d{2})$/i);
  if (shortAlpha) {
    const year = format(new Date(occurredAt), 'yyyy');
    const month = MONTH_MAP[shortAlpha[1].toLowerCase()] ?? pad(shortAlpha[1]);
    return `${year}-${month}-${pad(shortAlpha[2])} ${shortAlpha[3]}`;
  }

  // MM/DD/YYYY HH:MM:SS  ou  MM-DD-YYYY HH:MM:SS
  const fullNum = rawTime.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\s+(\d{2}:\d{2}:\d{2})$/);
  if (fullNum) {
    return `${fullNum[3]}-${pad(fullNum[1])}-${pad(fullNum[2])} ${fullNum[4]}`;
  }

  // MM/DD HH:MM:SS  ou  MM-DD HH:MM:SS  (sem ano)
  const shortNum = rawTime.match(/^(\d{1,2})[\/\-](\d{1,2})\s+(\d{2}:\d{2}:\d{2})$/);
  if (shortNum) {
    const year = format(new Date(occurredAt), 'yyyy');
    return `${year}-${pad(shortNum[1])}-${pad(shortNum[2])} ${shortNum[3]}`;
  }

  // HH:MM:SS  (só hora — usa a data do occurredAt)
  const timeOnly = rawTime.match(/^(\d{2}:\d{2}:\d{2})$/);
  if (timeOnly) return `${fallbackDate} ${timeOnly[1]}`;

  return fallbackFull;
};

const eventLabels: Record<string, string> = {
  login_attempt: 'Tentativa login',
  login_success: 'Login',
  logout: 'Logout',
  password_error: 'Erro senha',
  login_failed: 'Falha login',
  session_limit: 'Max sessoes',
  limit_reached: 'Limite estourado',
  other: 'Outro'
};

const badgeClasses: Record<string, string> = {
  login_attempt: 'bg-blue-100 text-blue-800',
  login_success: 'bg-green-100 text-green-800',
  logout: 'bg-slate-100 text-slate-800',
  password_error: 'bg-red-100 text-red-800',
  login_failed: 'bg-rose-100 text-rose-800',
  session_limit: 'bg-amber-100 text-amber-800',
  limit_reached: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-700'
};

const LogPage: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [events, setEvents] = useState<EventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [period, setPeriod] = useState<Period>('24h');
  const [companyId, setCompanyId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [eventType, setEventType] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const requestConfig: LogsRequestConfig = useMemo(
    () => ({
      preserveSessionOnAuthError: true
    }),
    []
  );

  const queryParams = useMemo(
    () => ({
      companyId: companyId || undefined,
      branchId: branchId || undefined,
      eventType: eventType || undefined,
      search: search.trim() || undefined,
      period,
      startDate: period === 'custom' ? startDate || undefined : undefined,
      endDate: period === 'custom' ? endDate || undefined : undefined,
      page,
      pageSize: 50
    }),
    [branchId, companyId, endDate, eventType, page, period, search, startDate]
  );

  const periodLabel = {
    '24h': 'Ultimas 24 horas',
    '7d': 'Ultimos 7 dias',
    '30d': 'Ultimos 30 dias',
    custom: 'Periodo customizado'
  }[period];

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies', requestConfig);
      const payload = response.data || [];
      setCompanies(payload);

      if (payload.length === 1 && !companyId) {
        setCompanyId(payload[0].id);
      }
    } catch (error) {
      toast.error('Failed to load companies');
    }
  };

  const loadBranches = async (selectedCompanyId: string) => {
    try {
      const response = await api.get('/branches', {
        ...requestConfig,
        params: {
          companyId: selectedCompanyId || undefined
        }
      });
      setBranches(response.data || []);
    } catch (error) {
      toast.error('Failed to load vessels');
    }
  };

  const loadLogs = async (isBackground = false) => {
    if (period === 'custom' && (!startDate || !endDate)) {
      setLoading(false);
      return;
    }

    if (!isBackground) {
      setRefreshing(true);
    }

    try {
      const [summaryRes, eventsRes] = await Promise.all([
        api.get('/logs/hotspot/summary', { ...requestConfig, params: queryParams }),
        api.get('/logs/hotspot/events', { ...requestConfig, params: queryParams })
      ]);

      setSummary(summaryRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load hotspot logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (user?.role === 'COMPANY_MANAGER' && user.companyId && !companyId) {
      setCompanyId(user.companyId);
    }
  }, [companyId, user]);

  useEffect(() => {
    loadBranches(companyId);
  }, [companyId]);

  useEffect(() => {
    setBranchId('');
    setPage(1);
  }, [companyId]);

  useEffect(() => {
    setPage(1);
  }, [branchId, eventType, search, period, startDate, endDate]);

  useEffect(() => {
    loadLogs();
  }, [queryParams]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadLogs(true);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [queryParams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">LOG</h1>
          <p className="text-sm text-gray-500">Eventos hotspot. {periodLabel}.</p>
        </div>
        <button
          onClick={() => loadLogs()}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:bg-gray-400"
        >
          <RefreshCw size={16} />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 text-gray-700 mb-4">
          <Filter size={16} />
          <h2 className="text-sm font-semibold uppercase tracking-wide">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Periodo</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="24h">24h</option>
              <option value="7d">7 dias</option>
              <option value="30d">30 dias</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Empresa</label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Todas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Vessel</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Todos</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.company?.name ? `${branch.company.name} - ` : ''}{branch.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Evento</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="all">Todos</option>
              {Object.entries(eventLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Busca</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="usuario, IP, mensagem"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>

        {period === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Inicio</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Fim</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.total || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Tentativas</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.loginAttempt || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Login</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.loginSuccess || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Logout</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.logout || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Erro senha</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.passwordError || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Max sessoes</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.sessionLimit || 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Limite</p>
          <p className="text-2xl font-semibold text-gray-900">{summary?.limitReached || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <Activity size={16} className="text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-800">Eventos hotspot</h3>
          <div className="ml-auto text-xs text-gray-500">
            {summary?.lastEventAt ? `Ultimo evento: ${format(new Date(summary.lastEventAt), 'yyyy-MM-dd HH:mm:ss')}` : 'Sem eventos'}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa / Vessel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topics</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mensagem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(events?.items || []).map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                    {formatLogTime(item.rawTime, item.occurredAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {item.branch.company.name} / {item.branch.name}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badgeClasses[item.eventType] || badgeClasses.other}`}>
                      {eventLabels[item.eventType] || item.eventType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.username || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.ipAddress || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.topics}</td>
                  <td className="px-4 py-3 text-sm text-gray-800 min-w-[380px]">{item.message}</td>
                </tr>
              ))}
              {(events?.items?.length || 0) === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                    Sem logs hotspot para o filtro selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Pagina {events?.page || 1} de {events?.totalPages || 1} • {events?.total || 0} registros
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={(events?.page || 1) <= 1}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((current) => Math.min(events?.totalPages || current, current + 1))}
              disabled={(events?.page || 1) >= (events?.totalPages || 1)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
            >
              Proxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogPage;
